import * as React from "react"
import {graphql, StaticQuery, navigate} from "gatsby";
import {GatsbyImage} from "gatsby-plugin-image";

const Invites = () => {
  const [selectedCategory, setSelectedCategory] = React.useState("全部");

  React.useEffect(() => {
    // Read category from URL on mount
    const params = new URLSearchParams(window.location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      setSelectedCategory(decodeURIComponent(categoryParam));
    }
  }, []);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    // Update URL when category changes
    const newUrl = category === "全部" 
      ? window.location.pathname 
      : `${window.location.pathname}?category=${encodeURIComponent(category)}`;
    navigate(newUrl, { replace: true });
  };

  // Curated primary categories, in display order. Any category found in the
  // data but not listed here is appended at the end so nothing is hidden.
  const CATEGORY_ORDER = [
    "VPS主机",
    "券商",
    "加密货币",
    "金融银行",
    "转运邮箱",
    "日本生活",
    "工具服务",
  ];

  const getUniqueCategories = (invites) => {
    const present = new Set(invites.map(invite => invite.category).filter(Boolean));
    const ordered = CATEGORY_ORDER.filter(c => present.has(c));
    const extras = [...present].filter(c => !CATEGORY_ORDER.includes(c));
    return ["全部", ...ordered, ...extras];
  };

  return (
    <StaticQuery
      query={graphql`
        query InvitesQuery {
          contentYaml {
            invites {
              id
              category
              description
              link
              introduce
              details
              name
              tags
              logo {
                childImageSharp {
                  gatsbyImageData(
                    layout: FIXED
                    width: 56
                    height: 56
                    quality: 95
                    transformOptions: {fit: CONTAIN}
                    backgroundColor: "transparent"
                  )
                }
              }
            }
          }
        }
      `}
      render={data => 
        { 
          const categories = getUniqueCategories(data.contentYaml.invites);
          const filteredInvites = data.contentYaml.invites.filter(invite =>
            selectedCategory === "全部" || invite.category === selectedCategory
          );

          return (
        <div>
          {/* Category filter bar */}
          <div className="sticky top-0 z-10 -mx-4 mb-6 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur supports-[backdrop-filter]:bg-gray-100/60">
            <div className="relative">
              <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-3">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    className={`shrink-0 whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedCategory === category
                        ? "bg-blue-600 text-white shadow-sm"
                        : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-700"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
              {/* fade hint on the right edge */}
              <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-gray-100 to-transparent dark:from-gray-900" />
            </div>
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filteredInvites.map((invite) => {

            return (
              <div
                key={invite.id}
                className="group flex flex-col rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:border-gray-200 dark:bg-gray-900 dark:border-gray-800 dark:hover:border-gray-700"
              >
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-start gap-4">
                    <a href={`${invite.link}`} className="shrink-0">
                      <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl bg-gray-50 ring-1 ring-gray-100 dark:bg-gray-800 dark:ring-gray-700">
                        <GatsbyImage
                          alt={invite.name}
                          image={invite.logo.childImageSharp.gatsbyImageData}
                        />
                      </div>
                    </a>
                    <div className="min-w-0 flex-1">
                      <a href={`${invite.link}`}>
                        <h4 className="text-lg font-semibold text-gray-900 transition-colors group-hover:text-blue-600 dark:text-gray-100">
                          {invite.name}
                        </h4>
                      </a>
                      <p className="mt-0.5 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
                        {invite.description}
                      </p>
                    </div>
                  </div>

                  <ul className="mt-4 space-y-1.5">
                    {invite.details.map(detail => {
                      return (
                        <li key={detail} className="flex gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-blue-400" />
                          <span>{detail}</span>
                        </li>
                      )
                    })}
                  </ul>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {invite.tags.map(tag => {
                      return (
                        <span
                          key={tag}
                          className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                        >
                          {tag}
                        </span>
                      )
                    })}
                  </div>
                </div>

                <div className="mt-auto flex gap-2 border-t border-gray-100 p-4 dark:border-gray-800">
                  <a
                    className="flex-1 rounded-lg px-3 py-2 text-center text-sm font-medium text-gray-700 ring-1 ring-gray-200 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-800"
                    href={`${invite.introduce}`}
                  >
                    查看介绍
                  </a>
                  <a
                    className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-center text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
                    href={`${invite.link}`}
                    target="_blank"
                    rel="noopener nofollow sponsored"
                  >
                    立即注册
                  </a>
                </div>
              </div>
            )
          })}
          </div>
        </div>
      )}
    }
    />
  )
}

export default Invites
